<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { routes } from '@/components/router/index.js'
import { find_menu_open_keys } from './menu_item.vue'
import MenuItemRecursive from './menu_item.vue'

const route = useRoute()
const router_list = ref([])
const collapsed_keys = ref(new Set())
const manual_open_keys = ref(new Set())

// ===== created =====
init_layout()


// ===== computed =====

// 导航栏选项
const selectedKeys = computed(get_selected_keys)
// 导航栏展开选项（路由自动展开 + 记住用户手动收起）
const route_open_keys = computed(get_route_open_keys)
const openkeys = computed(get_open_keys)


// ===== computed functions =====

/**
 * Get the currently selected menu key based on the active route path.
 * @returns {Array} Single-element array containing the current route path
 */
function get_selected_keys() {
    return [route.path]
}


/**
 * Compute the submenu keys that should auto-expand based on the current route.
 * Traverses the menu tree to find ancestor keys of the active route.
 * @returns {Array} Array of submenu keys that match the current route
 */
function get_route_open_keys() {
    return find_menu_open_keys(router_list.value, route.path, '') || []
}


/**
 * Compute the final set of open submenu keys by merging route-driven keys
 * and user-manually-opened keys, then excluding user-manually-collapsed keys.
 * Formula: (route_open_keys ∪ manual_open_keys) − collapsed_keys
 * @returns {Array} Array of submenu keys that should currently be open
 */
function get_open_keys() {
    let keys = new Set([...route_open_keys.value, ...manual_open_keys.value])
    let result = []
    for (let key of keys) {
        if (!collapsed_keys.value.has(key)) { result.push(key) }
    }
    return result
}


// ===== methods =====

/**
 * Handle menu open state changes from user interaction.
 * Detects manually collapsed keys by comparing reported keys against expected route_open_keys.
 * @param {Array} reportedKeys - The open keys reported by Ant Design after user interaction
 */
function on_open_change(reportedKeys) {
    let expected = new Set(route_open_keys.value)
    let reported = new Set(reportedKeys)
    let nextCollapsed = new Set()
    let nextManualOpen = new Set()

    // Keys that should be open (route) but user collapsed them
    for (let key of expected) {
        if (!reported.has(key)) {
            nextCollapsed.add(key)
        }
    }

    // Keys user manually opened that aren't required by route
    for (let key of reported) {
        if (!expected.has(key)) {
            nextManualOpen.add(key)
        }
    }

    collapsed_keys.value = nextCollapsed
    manual_open_keys.value = nextManualOpen
}

/**
 * Initialize the sidebar navigation menu.
 * Reads the global route configuration, deep clones it, and filters out hidden menu items (`hidemenu`)
 * and routes without child nodes. Recursively builds the menu tree and assigns it
 * to the `router_list` reactive variable to drive the sidebar menu rendering.
 */
function init_layout() {
    let result = []
    for (let index = 0; index < routes.length; index++) {
        if (routes[index]['hidemenu']) continue
        if (!routes[index]['children']) continue
        result = result.concat(build_menu_items(routes[index]['children']))
    }
    router_list.value = result
}


/**
 * Recursively build menu items, filter out `hidemenu` routes, and retain the `children`
 * structure of visible sub-items.
 * @param {Array} items - Array of route configurations
 * @returns {Array} Array of filtered menu items
 */
function build_menu_items(items) {
    if (!items || !items.length) return []
    let result = []
    for (let index = 0; index < items.length; index++) {
        let item = items[index]
        if (item['hidemenu']) continue
        let cloned = { ...item }
        cloned['children'] = build_menu_items(item['children'])
        result.push(cloned)
    }
    return result
}
</script>


<template>
    <div class="main-menu-wrapper">
        <a-menu theme="dark" mode="inline" :selectedKeys="selectedKeys" :openKeys="openkeys" @openChange="on_open_change">
            <MenuItemRecursive :items="router_list" />
        </a-menu>
    </div>
</template>


<style scoped>

.main-menu-wrapper :deep(.ant-menu-dark .ant-menu-item),
.main-menu-wrapper :deep(.ant-menu-dark .ant-menu-submenu-title) {
    border-radius: 0.2em !important;
    font-family: 'GoogleSans', sans-serif;
}

.main-menu-wrapper :deep(.ant-menu-dark .ant-menu-item.ant-menu-item-selected) {
    border-radius: 0.2em !important;
    background-color: #3878cf !important;
    color: white !important;
    font-family: 'GoogleSans', sans-serif;
}


.main-menu-wrapper :deep(.ant-menu-dark .ant-menu-item:hover),
.main-menu-wrapper :deep(.ant-menu-dark .ant-menu-submenu-title:hover) {
    background-color: rgba(56, 120, 207, 0.45) !important;
    color: #ffffff !important;
    box-shadow: 0 0 6px #3877cd !important;
}


</style>
